import React, { useCallback, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import AccountLayout from 'components/layout/account';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import { NavigationAwareBackArrow } from 'components/navigation/back-arrow';
import { RecentItems } from 'components/recent-items';
import {
  RefreshButton,
  RefreshButtonContainer,
  StyledRefreshIcon
} from 'components/refresh-button/';
import { useRecentItemsAvailable } from 'hooks/use-recent-items-available';
import { useUserOrders } from 'hooks/use-user-orders';
import AuthRequired from 'pages/authentication/auth-required';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import DetailsModal from './details-modal';
import { messages } from './messages';
import Order from './order';
import OrdersEmpty from './orders-empty';

export enum MODAL_TYPE {
  SHOW_DETAILS = 'showDetails',
  TRACK_ORDER = 'trackOrder'
}

const OrdersContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  max-width: ${Styles.layout.sectionMaxWidth};
  padding-bottom: 0;
  padding-top: 0;
  width: calc(100% - 2rem);
  & > div {
    padding-top: 0px;
  }
`;

const OrdersEmptyWrapper = styled.div`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const LoadingContainerWrapper = styled(LoadingContainer)`
  width: unset;
`;

const OrdersPanel = styled.div`
  width: 100%;
  ${Styles.desktop`
    padding-top: 2rem;
  `};
`;

const OfferDisclaimer = styled.div`
  text-align: center;
`;

const Heading = styled.h2`
  font-family: ${Styles.fontFamily.base};
  text-transform: uppercase;
  margin: 2rem 0;
  margin-bottom: 20px;
  color: ${Styles.color.black} !important;
  font-size: 2.25rem;
  text-align: center;
  ${Styles.desktop`
    font-size: 3.5rem;
  `}
`;

const AccountOrderWrapper = styled.div`
  ${Styles.desktop`
    padding-top: 150px;
  `}
`;

const AccountOrders: React.FC = () => {
  const { user } = useAuthContext();
  const [detailsModal, setDetailsModal] = useState('');
  const { loading, orders, refetch } = useUserOrders(user);
  const handleDisplayDetailsModal = useCallback(orderId => setDetailsModal(orderId), []);
  const [modalType, setModalType] = useState<string>(MODAL_TYPE.SHOW_DETAILS);
  const { formatMessage } = useIntl();
  const showItems = useRecentItemsAvailable();
  const onRefreshClick = useCallback(() => {
    refetch();
  }, [refetch]);
  const enableRecentItemsAddToCart = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ITEMS_ADD_TO_CART);

  const refreshButton = (
    <RefreshButtonContainer>
      <RefreshButton onClick={onRefreshClick}>
        <StyledRefreshIcon />
        {formatMessage(messages.refreshOrders)}
      </RefreshButton>
    </RefreshButtonContainer>
  );
  const hasOrders = !!orders.length;
  return (
    <AccountOrderWrapper>
      <AuthRequired>
        <Helmet title={formatMessage(messages.recentOrders)} />
        {showItems && (
          <OrdersContainer>
            <NavigationAwareBackArrow color={Styles.color.headerContrast} />
            {enableRecentItemsAddToCart && (
              <>
                <Heading>{formatMessage(messages.recentItems)}</Heading>
                <RecentItems locationName="Recent Orders Page" />
              </>
            )}
          </OrdersContainer>
        )}
        {loading ? (
          <LoadingContainerWrapper>
            <LoadingAnimation fillColor={Styles.color.primary} />
          </LoadingContainerWrapper>
        ) : (
          <OrdersContainer>
            {hasOrders ? (
              <AccountLayout fullWidth>
                <Heading>{formatMessage(messages.recentOrders)}</Heading>
                <OfferDisclaimer>{formatMessage(messages.recentOrdersDisclaimer)}</OfferDisclaimer>
                {refreshButton}
                <OrdersPanel>
                  {orders.map(order => (
                    <Order
                      key={order.rbiOrderId}
                      order={order}
                      handleDisplayDetailsModal={handleDisplayDetailsModal}
                      setModalType={setModalType}
                    />
                  ))}
                </OrdersPanel>
              </AccountLayout>
            ) : (
              <OrdersEmptyWrapper>
                <OrdersEmpty>{refreshButton}</OrdersEmpty>
              </OrdersEmptyWrapper>
            )}
          </OrdersContainer>
        )}
        {detailsModal && (
          <DetailsModal
            rbiOrderId={detailsModal}
            onModalDismiss={handleDisplayDetailsModal}
            type={modalType}
            setModalType={setModalType}
          />
        )}
      </AuthRequired>
    </AccountOrderWrapper>
  );
};

export default AccountOrders;
